import { action, makeObservable, observable, runInAction } from 'mobx';
import { ApiError } from 'shared/types';

type AsyncStatus = 'idle' | 'pending' | 'done' | 'error';

export class AsyncState<T> {
   status: AsyncStatus = 'idle';
   data: T | null = null;
   error: ApiError | null = null;

   constructor() {
      makeObservable(this, {
         status: observable,
         data: observable,
         error: observable,
         run: action.bound,
      });
   }

   async run(
      promiseFn: () => Promise<T>,
      opts?: {
         onSuccess?: (data: T) => void;
         onError?: (error: ApiError) => void;
         onFinally?: () => void;
      }
   ) {
      runInAction(() => {
         this.status = 'pending';
         this.error = null;
      });

      try {
         const result = await promiseFn();

         runInAction(() => {
            this.data = result;
            this.status = 'done';
         });

         opts?.onSuccess?.(result);
         return result;
      } catch (err) {
         runInAction(() => {
            this.error = err as ApiError;
            this.status = 'error';
         });

         opts?.onError?.(err as ApiError);
         throw err;
      } finally {
         opts?.onFinally?.();
      }
   }
}
